import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Tag, Space, Card, InputNumber } from 'antd';
import { UserAddOutlined, EditOutlined, DeleteOutlined, TeamOutlined, SlidersOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from './auth';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [limitsModalVisible, setLimitsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [limitsForm] = Form.useForm();
  const navigate = useNavigate();

  const fetchUsers = () => {
    setLoading(true);
    axios.get('/api/auth/users')
      .then(res => {
        if (res.data.success) {
          setUsers(res.data.users);
        }
      })
      .catch(() => {
        message.error('获取用户列表失败');
        navigate('/login');
        window.location.reload();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAdd = () => {
    setEditUser(null);
    form.resetFields();
    form.setFieldsValue({ image_limit: 3, video_limit: 3 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditUser(record);
    form.setFieldsValue({ username: record.username, nickname: record.nickname });
    setModalVisible(true);
  };

  const handlePassword = (record) => {
    setEditUser(record);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleLimits = (record) => {
    setEditUser(record);
    limitsForm.resetFields();
    limitsForm.setFieldsValue({
      image_limit: record.image_limit ?? 3,
      video_limit: record.video_limit ?? 3,
    });
    setLimitsModalVisible(true);
  };

  const handleDelete = (record) => {
    axios.delete(`/api/auth/users/${record.id}`)
      .then(res => {
        if (res.data.success) {
          message.success('删除成功');
          fetchUsers();
        } else {
          message.error(res.data.error || '删除失败');
        }
      })
      .catch(() => message.error('删除失败'));
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      if (editUser) {
        // 编辑用户名（暂时不做用户名修改，只提供入口）
        message.error('用户名暂不支持修改');
        return;
      }
      // 新增用户
      axios.post('/api/auth/users', values)
        .then(res => {
          if (res.data.success) {
            message.success('添加成功');
            setModalVisible(false);
            fetchUsers();
          } else {
            message.error(res.data.error || '添加失败');
          }
        })
        .catch(err => {
          message.error(err.response?.data?.error || '添加失败');
        });
    });
  };

  const handlePasswordOk = () => {
    passwordForm.validateFields().then(values => {
      axios.put(`/api/auth/users/${editUser.id}/password`, values)
        .then(res => {
          if (res.data.success) {
            message.success('密码修改成功');
            setPasswordModalVisible(false);
          } else {
            message.error(res.data.error || '修改失败');
          }
        })
        .catch(err => {
          message.error(err.response?.data?.error || '修改失败');
        });
    });
  };

  const handleLimitsOk = () => {
    limitsForm.validateFields().then(values => {
      axios.put(`/api/auth/users/${editUser.id}/limits`, {
        image_limit: values.image_limit,
        video_limit: values.video_limit,
      })
        .then(res => {
          if (res.data.success) {
            message.success('次数限制修改成功');
            setLimitsModalVisible(false);
            fetchUsers();
          } else {
            message.error(res.data.error || '修改失败');
          }
        })
        .catch(err => {
          message.error(err.response?.data?.error || '修改失败');
        });
    });
  };

  const currentUserId = parseInt(localStorage.getItem('user_id') || '0');

  const formatLimit = (limit, used) => {
    const l = limit ?? -1;
    const u = used ?? 0;
    if (l === -1) return `已用 ${u} / 无限`;
    return `已用 ${u} / ${l}`;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '角色',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (val) => val === 1 ? <Tag color="blue">管理员</Tag> : <Tag color="default">普通用户</Tag>,
    },
    {
      title: '生图次数',
      key: 'image_limit',
      render: (_, record) => formatLimit(record.image_limit, record.image_used),
    },
    {
      title: '生视频次数',
      key: 'video_limit',
      render: (_, record) => formatLimit(record.video_limit, record.video_used),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => new Date(val).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handlePassword(record)}
          >
           改密
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SlidersOutlined />}
            onClick={() => handleLimits(record)}
          >
           次数
          </Button>
          {record.id !== currentUserId ? (
            <Popconfirm
              title="确定删除该用户？"
              onConfirm={() => handleDelete(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          ) : (
            <span style={{ color: '#999', fontSize: 12 }}>当前用户</span>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <div className="page-toolbar">
        <div>
          <div className="page-kicker">系统设置</div>
          <h1><TeamOutlined /> 用户管理</h1>
        </div>
        <Space>
          <Button type="primary" icon={<UserAddOutlined />} onClick={handleAdd}>
            新增用户
          </Button>
        </Space>
      </div>

      <Card className="admin-card">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' }
            ]}
          >
            <Input placeholder="请输入用户名" disabled={!!editUser} />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="昵称"
            rules={[
              { required: true, message: '请输入昵称' }
            ]}
          >
            <Input placeholder="请输入昵称" />
          </Form.Item>
          {!editUser && (
            <>
              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password placeholder="请输入密码" />
              </Form.Item>
              <Form.Item
                name="image_limit"
                label="初始生图次数（-1 = 无限）"
                initialValue={3}
                rules={[{ required: true, message: '请输入次数' }]}
              >
                <InputNumber style={{ width: '100%' }} min={-1} placeholder="默认3次" />
              </Form.Item>
              <Form.Item
                name="video_limit"
                label="初始生视频次数（-1 = 无限）"
                initialValue={3}
                rules={[{ required: true, message: '请输入次数' }]}
              >
                <InputNumber style={{ width: '100%' }} min={-1} placeholder="默认3次" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onOk={handlePasswordOk}
        onCancel={() => setPasswordModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p style={{ marginBottom: 16 }}>为用户 <strong>{editUser?.username}</strong> 设置新密码：</p>
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改次数限制"
        open={limitsModalVisible}
        onOk={handleLimitsOk}
        onCancel={() => setLimitsModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p style={{ marginBottom: 16 }}>为用户 <strong>{editUser?.username}</strong> 设置次数上限（-1 = 无限）：</p>
        <Form form={limitsForm} layout="vertical">
          <Form.Item
            name="image_limit"
            label="生图次数上限"
            rules={[{ required: true, message: '请输入次数' }]}
          >
            <InputNumber style={{ width: '100%' }} min={-1} placeholder="-1 = 无限" />
          </Form.Item>
          <Form.Item
            name="video_limit"
            label="生视频次数上限"
            rules={[{ required: true, message: '请输入次数' }]}
          >
            <InputNumber style={{ width: '100%' }} min={-1} placeholder="-1 = 无限" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UserManagement;
